import axios from "axios";

// API is served from the same host as the app (/api), so use the current origin.
// This keeps the app working on every address (emergent.host, slmagencies.com, preview).
const BACKEND_URL = typeof window !== "undefined" && window.location?.origin
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// When an approved customer is blocked/un-approved during their session, the
// backend responds 403 ACCESS_DENIED. Broadcast it so AuthContext can log them out.
// Short-lived access cookie is renewed silently from the long-lived refresh cookie,
// so users stay logged in until they press Logout.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (error.response?.status === 403 && error.response?.data?.detail === "ACCESS_DENIED") {
      window.dispatchEvent(new CustomEvent("slm:access-denied"));
    }
    const cfg = error.config;
    const url = cfg?.url || "";
    if (error.response?.status === 401 && cfg && !cfg._retried && !/\/auth\/(login|refresh|logout)/.test(url)) {
      cfg._retried = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
        await refreshing;
        return api(cfg);
      } catch {
        // fall through: session really expired
      }
    }
    return Promise.reject(error);
  }
);

// size: "sm" (thumbnails), "md" (product cards), undefined = original
// trimmed=true -> product variant with outer background trimmed (uniform catalogue scale)
export function productImageUrl(imagePath, size, trimmed = false) {
  if (!imagePath) return null;
  return `${API}/files/${imagePath}${size ? `?size=${size}${trimmed ? "&v=2" : ""}` : ""}`;
}

export function formatMoney(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function effectivePrice(p) {
  if (!p) return null;
  return p.offer_price !== null && p.offer_price !== undefined ? p.offer_price : p.price;
}

export const PRODUCT_UNITS = [
  "Piece", "Case", "Kg", "Bag", "Dozen", "Litre", "Pouch",
  "Sachet", "Tin", "Jar", "Bottle", "Box", "Packet",
];

// Build the list of purchasable price options for a product.
// If the product has explicit unit rates, sell by those units only.
// Otherwise fall back to the base Selling/Offer price ("Each").
// Only rates greater than zero are shown; blank/zero/invalid rates are hidden.
// Each option: { unit: string|null, label: string, rate: number }
export function priceOptions(product) {
  if (!product) return [];
  const valid = (r) =>
    r !== null && r !== undefined && r !== "" && !Number.isNaN(Number(r)) && Number(r) > 0;

  const unitOpts = (product.unit_rates || [])
    .filter((ur) => ur && valid(ur.rate))
    .map((ur) => ({ unit: ur.unit, label: ur.unit, rate: Number(ur.rate) }));
  if (unitOpts.length > 0) return unitOpts;

  const eff = effectivePrice(product);
  if (valid(eff)) {
    return [{ unit: null, label: product.pack_size || "Each", rate: Number(eff) }];
  }
  return [];
}

// Resolve the MRP to display for the selected unit.
// - Uses the unit-specific MRP entered by the admin (product.unit_mrps) when present.
// - Otherwise falls back to the general product.mrp so any product with an MRP
//   entered in Admin always shows it, regardless of the selected unit.
export function unitMrpFor(product, unit) {
  if (!product) return null;
  const valid = (r) =>
    r !== null && r !== undefined && r !== "" && !Number.isNaN(Number(r)) && Number(r) > 0;
  const match = (product.unit_mrps || []).find((m) => m && m.unit === unit && valid(m.mrp));
  if (match) return Number(match.mrp);
  if (valid(product.mrp)) return Number(product.mrp);
  return null;
}

export const OFFER_TYPES = [
  { value: "price", label: "Price Offer" },
  { value: "discount", label: "Discount" },
  { value: "free_item", label: "Free Item" },
  { value: "quantity", label: "Quantity Offer" },
  { value: "custom", label: "Custom Offer" },
];

export const OFFER_UNITS = ["Piece", "Case", "Box", "Bag", "Tin", "Dozen", "Packet", "Pouch"];

export function offerTypeLabel(type) {
  const m = OFFER_TYPES.find((o) => o.value === type);
  return m ? m.label : null;
}

// Build a human-readable offer line for a product's offer.
function fmtQty(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return "";
  return Number.isInteger(v) ? String(v) : String(v);
}
export function offerText(p) {
  if (!p || !p.has_offer) return null;
  const t = p.offer_type;
  if ((t === "free_item" || t === "quantity") && (p.offer_buy_qty || p.offer_free_qty)) {
    const buy = `Buy ${fmtQty(p.offer_buy_qty)} ${p.offer_buy_unit || ""}`.replace(/\s+/g, " ").trim();
    const free = `Get ${fmtQty(p.offer_free_qty)} ${p.offer_free_unit || ""} ${p.offer_free_item || "item"} Free`
      .replace(/\s+/g, " ")
      .trim();
    return `${buy} → ${free}`;
  }
  return p.offer_description || null;
}

// Format an ISO date (YYYY-MM-DD) to DD-MM-YYYY for display.
export function formatOfferDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
}

export function validityText(start, end) {
  const s = formatOfferDate(start);
  const e = formatOfferDate(end);
  if (s && e) return `Offer Valid: ${s} to ${e}`;
  if (s) return `Offer Valid from: ${s}`;
  if (e) return `Offer Valid until: ${e}`;
  return null;
}

export function offerValidityText(p) {
  if (!p) return null;
  return validityText(p.offer_start_date, p.offer_end_date);
}

// ---- Brand offers (applied dynamically, never stored on the product) ----
export function activeBrandOffer(product) {
  const bo = product && product.brand_offer;
  return bo && bo.active ? bo : null;
}

export function brandOfferText(bo) {
  if (!bo) return null;
  const o = {
    buy_qty: Number(bo.offer_buy_qty) || 1,
    buy_unit: bo.offer_buy_unit,
    cash_discount: Number(bo.offer_cash_discount) || 0,
    free_qty: Number(bo.offer_free_qty) || 0,
    free_unit: bo.offer_free_unit,
    free_item: bo.offer_free_item,
  };
  if (o.buy_unit && (o.cash_discount > 0 || o.free_qty > 0)) return offerBuyText(o);
  // legacy fallback
  if (bo.discount_kind === "percent") return `${bo.discount_value}% OFF on every product`;
  if (bo.discount_value) return `${formatMoney(bo.discount_value)} OFF on every product`;
  return bo.description || null;
}

export function brandOfferValidityText(bo) {
  if (!bo) return null;
  return validityText(bo.start_date, bo.end_date);
}

// Discounted rate for a given selling rate under a brand DISCOUNT offer.
export function brandDiscountedRate(rate, bo) {
  if (!bo || bo.offer_type !== "discount" || rate == null) return null;
  const base = Number(rate);
  if (Number.isNaN(base)) return null;
  let out;
  if (bo.discount_kind === "percent") out = base * (1 - Number(bo.discount_value) / 100);
  else out = base - Number(bo.discount_value);
  return Math.max(0, Math.round(out * 100) / 100);
}

// ---- Unified "Buy X unit → cash discount + free item" offer (product or brand) ----
function isDateActive(start, end) {
  const t = new Date().toISOString().slice(0, 10);
  if (start && t < start) return false;
  if (end && t > end) return false;
  return true;
}

export function applicableOffer(product) {
  if (!product) return null;
  const norm = (src, start, end) => {
    if (!src) return null;
    const bu = src.offer_buy_unit;
    if (!bu || !isDateActive(start, end)) return null;
    const cd = Number(src.offer_cash_discount) || 0;
    const fq = Number(src.offer_free_qty) || 0;
    if (cd <= 0 && fq <= 0) return null;
    return {
      buy_qty: Number(src.offer_buy_qty) || 1,
      buy_unit: bu,
      cash_discount: cd,
      free_qty: fq,
      free_unit: src.offer_free_unit,
      free_item: src.offer_free_item,
    };
  };
  const po = norm(product, product.offer_start_date, product.offer_end_date);
  if (po) return { ...po, scope: "product" };
  const bo = product.brand_offer;
  if (bo) {
    const b = norm(bo, bo.start_date, bo.end_date);
    if (b) return { ...b, scope: "brand" };
  }
  return null;
}

export function offerBuyText(o) {
  if (!o) return null;
  const parts = [];
  if (o.cash_discount > 0) parts.push(`${formatMoney(o.cash_discount)} OFF`);
  if (o.free_qty > 0 && o.free_item)
    parts.push(`${o.free_qty} ${o.free_unit || ""} ${o.free_item} FREE`.replace(/\s+/g, " ").trim());
  return `Buy ${o.buy_qty} ${o.buy_unit} → ${parts.join(" + ")}`;
}

export function computeLineOffer(o, unit, qty) {
  if (!o || o.buy_unit !== unit || o.buy_qty < 1) return null;
  const times = Math.floor(Number(qty) / o.buy_qty);
  if (times <= 0) return null;
  return {
    times,
    cashOff: times * (o.cash_discount || 0),
    freeQty: times * (o.free_qty || 0),
    freeUnit: o.free_unit,
    freeItem: o.free_item,
  };
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
